import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from './Header';
import SideNav from './SideNav';
import Footer from './Footer';
import PageHeader from './PageHeader';
import { speakWithAvatar, stopAvatarSpeech, getCurrentAudio } from '../ttsPlayer';
import { startSTT, stopSTT } from '../sttClient';
import usePageTimeTracker from '../hooks/usePageTimeTracker';
import './Common.css';
import './BusinessConversation.css';

const BusinessConversation = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language = 'german', proficiencyLevel = 'beginner', businessScenario = '', businessCategory = '', avatar = '', scenarioId = '' } = location.state || {};

  const normalizeToCode = (lang) => {
    const map = { 'german': 'de', 'english': 'en', 'french': 'fr', 'spanish': 'es', 'italian': 'it', 'japanese': 'ja', 'de': 'de', 'en': 'en', 'fr': 'fr', 'es': 'es', 'it': 'it', 'ja': 'ja' };
    return map[lang?.toLowerCase()] || lang || 'de';
  };
  const [currentLanguage, setCurrentLanguage] = useState(normalizeToCode(language));
  const [currentLevel, setCurrentLevel] = useState(proficiencyLevel);

  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [duration, setDuration] = useState(0);
  const [started, setStarted] = useState(false);
  const [micActive, setMicActive] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [showDurationMenu, setShowDurationMenu] = useState(false);
  const [menuPos, setMenuPos] = useState({top: 0, left: 0});
  const [captionsOn, setCaptionsOn] = useState(true);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [suggestion, setSuggestion] = useState('');
  const [hint, setHint] = useState('');
  const [showHint, setShowHint] = useState(false);
  const [hasHint, setHasHint] = useState(false);
  const [agentResponse, setAgentResponse] = useState('');
  const [userText, setUserText] = useState('');
  const [conversationHistory, setConversationHistory] = useState([]);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [avatarMuted, setAvatarMuted] = useState(false);
  const [speakerLocked, setSpeakerLocked] = useState(false);
  const [hasResponse, setHasResponse] = useState(false);
  const lastReadResponse = useRef('');
  const [showTranscript, setShowTranscript] = useState(false);
  const [showPausePopup, setShowPausePopup] = useState(false);
  const [showSummaryLoading, setShowSummaryLoading] = useState(false);
  const timerRef = useRef(null);
  const endedRef = useRef(false);
  const messagesEndRef = useRef(null);
  const durationMenuRef = useRef(null);
  usePageTimeTracker('business-conversation', user);

  // Cleanup STT on unmount (page navigation)
  useEffect(() => {
    return () => { stopSTT(); };
  }, []);

  const breadcrumbs = [
    { label: 'Home', path: '/', current: false },
    { label: 'Explore Learning', path: '/explore-learning', current: false },
    { label: 'Business Scenarios', path: '/practice-business', current: false },
    { label: 'Practice Scenario', current: true }
  ];

  const formatTime = (s) => `${Math.floor(s/60).toString().padStart(2,'0')}:${(s%60).toString().padStart(2,'0')}`;

  const checkAuthError = (data) => {
    const err = data?.error || '';
    if (typeof err === 'string' && (err.includes('not authenticated') || err.includes('No user email'))) {
      window.location.href = '/auth/login?returnTo=' + encodeURIComponent(window.location.pathname);
      return true;
    }
    return false;
  };

  const getUserInitials = () => {
    if (!user || !user.name) return 'LU';
    const name = user.name.includes(',') ? user.name.split(',').reverse().map(s => s.trim()).join(' ') : user.name;
    const parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return parts[0].substring(0, 2).toUpperCase();
  };

  const getUserFirstName = () => {
    if (!user || !user.name) return 'You';
    if (user.name.includes(',')) return user.name.split(',')[1]?.trim()?.split(' ')[0] || 'You';
    return user.name.split(' ')[0] || 'You';
  };

  const isSoren = !avatar || avatar.includes('Max') || avatar.toLowerCase().includes('soren');
  const selectedAvatar = isSoren ? 'soren' : 'nebula';

  const avatarName = selectedAvatar === 'soren' ? 'Soren' : 'Nebula';

  const handleStartDiscussion = async () => {
    setStarted(true);
    timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
    setIsLoading(true);

    const langMap = { 'german': 'German', 'de': 'German', 'french': 'French', 'fr': 'French', 'spanish': 'Spanish', 'es': 'Spanish', 'italian': 'Italian', 'it': 'Italian', 'japanese': 'Japanese', 'ja': 'Japanese', 'english': 'English', 'en': 'English' };
    const langName = langMap[currentLanguage?.toLowerCase()] || 'German';

    const payload = {
      scenario_id: scenarioId || 'scenario_01_01',
      language: langName,
      dialect: 'standard',
      avatar: avatarName
    };

    console.log('Starting Business Scenario Agent with payload:', payload);

    try {
      const res = await fetch((process.env.REACT_APP_API_URL || '/lb-api') + '/business-scenario/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      console.log('Business Scenario Agent Response:', data);
      if (checkAuthError(data)) return;

      const agentData = data.data?.content || data.data || data;
      // Check if agent session expired
      if (agentData.error && String(agentData.error).includes('not found')) {
        setAgentResponse('Session expired. Please start a new discussion.');
        setIsLoading(false);
        return;
      }
      setSessionId(agentData.session_id || '');
      setSuggestion(agentData.suggestion || '');
      setHint(agentData.hint || '');
      setHasHint(!!agentData.hint);
      setShowHint(false);
      setAgentResponse(agentData.response || '');
      setHasResponse(true);
      saveConversationLog(avatarName, "", agentData.response, agentData.suggestion, agentData.hint);
      if (agentData.response) readResponse(agentData.response);
      setConversationHistory([{ type: "assistant", name: avatarName, text: agentData.response || "" }]);
    } catch (err) {
      console.error('Agent call failed:', err);
      setMessages([{ id: 1, type: 'assistant', text: `Hi! Let's practice "${businessScenario}" in ${langName}. (Agent unavailable)`, timestamp: new Date() }]);
    }
    setIsLoading(false);
  };

  const handleEndDiscussion = async () => {
    setShowEndConfirm(false);
    setShowSummaryLoading(true);
    endedRef.current = true; // Set first to prevent any callbacks from firing
    setStarted(false);
    if (timerRef.current) clearInterval(timerRef.current);
    // Stop microphone and camera
    setMicActive(false);
    if (window._bscRecognition) { window._bscRecognition.stop(); window._bscRecognition = null; }
    stopSTT();
    window._bscTranscript = '';
    window._bscFinalText = '';
    setUserText('');
    // Always stop camera stream
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    setCameraOn(false);
    // Stop any ongoing TTS audio
    stopAvatarSpeech();
    setIsSpeaking(false);
    // Call end conversation agent
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/lb-api';
      const res = await fetch(`${apiUrl}/business-scenario/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          session_id: sessionId,
          avatar: avatarName
        })
      });
      const data = await res.json();
      console.log('End conversation response:', data);
      if (data.success) {
        const c = data.data?.content || data.data || {};
        const s = c.summary || c;
        // corrections & progress_tracking can be at content level (c) OR inside summary (s)
        const rawCorrections = Array.isArray(c.corrections) ? c.corrections : (Array.isArray(s.corrections) ? s.corrections : []);
        const rawProgress = Array.isArray(c.progress_tracking) ? c.progress_tracking : (Array.isArray(s.progress_tracking) ? s.progress_tracking : []);
        console.log('Summary data path - c keys:', Object.keys(c), 's keys:', Object.keys(s));
        console.log('rawCorrections:', rawCorrections.length, 'rawProgress:', rawProgress.length);
        // Ensure all fields are primitives (not objects) to avoid React error #31
        const safe = {
          quick_summary: typeof s.quick_summary === 'string' ? s.quick_summary : JSON.stringify(s.quick_summary || ''),
          quote: typeof s.quote === 'string' ? s.quote : JSON.stringify(s.quote || ''),
          stage: Number(s.stage) || 0,
          stage_label: String(s.stage_label || ''),
          conversation_score: Number(s.conversation_score) || 0,
          level: String(s.level || ''),
          skill_breakdown: {
            vocabulary: Number(s.skill_breakdown?.vocabulary) || 0,
            grammar: Number(s.skill_breakdown?.grammar) || 0,
            pronunciation: Number(s.skill_breakdown?.pronunciation) || 0,
            fluency: Number(s.skill_breakdown?.fluency) || 0
          },
          strengths: Array.isArray(s.strengths) ? s.strengths.map(i => typeof i === 'string' ? i : (i?.text || i?.description || i?.point || JSON.stringify(i))) : [],
          areas_to_improve: Array.isArray(s.areas_to_improve) ? s.areas_to_improve.map(i => typeof i === 'string' ? i : (i?.text || i?.description || i?.point || JSON.stringify(i))) : [],
          corrections: rawCorrections.map(i => {
            if (typeof i === 'string') return i;
            if (i?.original && i?.corrected) return { turn: Number(i.turn) || 0, original: String(i.original), corrected: String(i.corrected) };
            return { turn: Number(i?.turn) || 0, original: String(i?.wrong_word || i?.text || JSON.stringify(i)), corrected: String(i?.correct_word || '') };
          }),
          progress_tracking: rawProgress.map(i => typeof i === 'string' ? i : (i?.text || i?.description || i?.point || JSON.stringify(i)))
        };
        // Store transcript from response for View Conversation Transcript
        const transcriptMapped = Array.isArray(c.transcript) ? c.transcript.map(t => ({
          type: t.role === 'agent' ? 'assistant' : 'user',
          name: t.role === 'agent' ? avatarName : getUserFirstName(),
          text: t.message || ''
        })) : conversationHistory;

        // Navigate to summary page
        navigate('/conversation-summary', {
          state: {
            summaryData: safe,
            audioBase64: c.audio_base64 || '',
            audioFormat: c.audio_format || 'mp3',
            transcript: transcriptMapped,
            language: currentLanguage,
            proficiencyLevel: currentLevel,
            businessScenario,
            businessCategory,
            avatarName,
            avatar,
            scenarioId,
            duration
          }
        });
        return;
      }
    } catch (err) {
      console.error('End conversation error:', err);
      setShowSummaryLoading(false);
    }
  };

  useEffect(() => {
    if (cameraOn && streamRef.current && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [cameraOn]);

  useEffect(() => { return () => { endedRef.current = true; if (timerRef.current) clearInterval(timerRef.current); stopAvatarSpeech(); stopSTT(); if (window._bscRecognition) { window._bscRecognition.stop(); window._bscRecognition = null; } if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; } }; }, []);
  useEffect(() => { if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  useEffect(() => {
    if (!showDurationMenu) return;
    const h = (e) => { if (durationMenuRef.current && !durationMenuRef.current.contains(e.target)) setShowDurationMenu(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showDurationMenu]);

  const handleSend = () => {
    if (!inputText.trim()) return;
    console.log('handleSend called, text:', inputText, 'sessionId:', sessionId);
    sendUserResponse(inputText);
    setInputText('');
  };

  const toggleMic = () => {
    console.log('toggleMic clicked, micActive:', micActive, 'sessionId:', sessionId);
    // Stop TTS when user starts speaking
    if (!micActive) {
      if (getCurrentAudio()) { getCurrentAudio().pause(); getCurrentAudio().currentTime = 0; }
      stopAvatarSpeech();
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      setAvatarMuted(true);
      setSpeakerLocked(true);
    }
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    if (micActive) {
      setMicActive(false);
      if (isLocalDev) {
        if (window._bscRecognition) { window._bscRecognition.stop(); window._bscRecognition = null; }
        const localText = window._bscTranscript || '';
        console.log('Local mic stopped, text:', localText);
        if (localText.trim()) { onMicStop(localText.trim()); }
        window._bscTranscript = '';
      } else {
        stopSTT();
        const rawText = window._bscTranscript; const finalText = Array.isArray(rawText) ? rawText.join(' ') : String(rawText || ''); // fallback: document.querySelector('.bsc-agent-response.user strong')?.parentElement?.textContent?.replace(/^[^:]+:\s*/, '') || '';
        console.log('Mic stopped, finalText:', finalText, '_bscTranscript:', window._bscTranscript);
        if (finalText.trim()) { onMicStop(finalText.trim()); }
        window._bscTranscript = '';
      }
      return;
    }

    // Production: use WebSocket STT
    if (!isLocalDev) {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/stt`;
      const langMap = { 'german': 'de-DE', 'de': 'de-DE', 'french': 'fr-FR', 'fr': 'fr-FR', 'spanish': 'es-ES', 'es': 'es-ES', 'italian': 'it-IT', 'it': 'it-IT', 'japanese': 'ja-JP', 'ja': 'ja-JP' };
      const sttLang = langMap[currentLanguage?.toLowerCase()] || 'en-US';
      window._bscFinalText = ''; window._bscTranscript = ''; setUserText(''); console.log('Starting WebSocket STT, lang:', sttLang);
      startSTT(wsUrl, { language: sttLang }, ({ text, isPartial: partial }) => {
        if (text) {
          const t = Array.isArray(text) ? text.join(' ') : String(text);
          if (t.trim()) {
            if (!partial) {
              // Final result — accumulate
              window._bscFinalText = (window._bscFinalText || '') + (window._bscFinalText ? ' ' : '') + t.trim();
              window._bscTranscript = window._bscFinalText;
              setUserText(window._bscFinalText);
              console.log('STT final, _bscFinalText:', window._bscFinalText);
            } else {
              // Partial result — show accumulated finals + current partial
              const base = window._bscFinalText || '';
              const display = base ? base + ' ' + t : t;
              window._bscTranscript = display;
              setUserText(display);
            }
          }
        }
      });
      setMicActive(true);
      return;
    }

    // Local: use Web Speech API
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { console.error('Speech recognition not supported'); return; }
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = currentLanguage === 'german' || currentLanguage === 'de' ? 'de-DE' : currentLanguage === 'french' || currentLanguage === 'fr' ? 'fr-FR' : currentLanguage === 'spanish' || currentLanguage === 'es' ? 'es-ES' : currentLanguage === 'italian' || currentLanguage === 'it' ? 'it-IT' : currentLanguage === 'japanese' || currentLanguage === 'ja' ? 'ja-JP' : 'en-US';
    recognition.onresult = (event) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      setUserText(transcript); window._bscTranscript = transcript;
    };
    recognition.onerror = (e) => { console.error('STT error:', e); setMicActive(false); };
    recognition.onend = () => { setMicActive(false); window._bscRecognition = null; if (window._bscTranscript) { onMicStop(window._bscTranscript); window._bscTranscript = ''; } };
    recognition.start();
    window._bscRecognition = recognition;
    window._bscTranscript = '';
    setUserText('');
    setMicActive(true);
  };

  const toggleCamera = async () => {
    // Stop TTS when user starts camera
    if (!cameraOn) {
      if (getCurrentAudio()) { getCurrentAudio().pause(); getCurrentAudio().currentTime = 0; }
      stopAvatarSpeech();
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      setAvatarMuted(true);
      setSpeakerLocked(true);
    }
    if (cameraOn) {
      if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
      setCameraOn(false);
      // Stop STT and send text (don't change mic icon)
      const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (isLocalDev) {
        if (window._bscRecognition) { window._bscRecognition.stop(); window._bscRecognition = null; }
      } else {
        stopSTT();
      }
      const rawText2 = window._bscTranscript; const finalText = Array.isArray(rawText2) ? rawText2.join(' ') : String(rawText2 || '');
      if (finalText.trim()) { onMicStop(finalText.trim()); }
      window._bscTranscript = '';
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        streamRef.current = stream;
        setCameraOn(true);
        // Start STT in background (don't change mic icon)
        const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        if (!isLocalDev) {
          const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
          const wsUrl = `${protocol}//${window.location.host}/stt`;
          const langMap = { 'german': 'de-DE', 'de': 'de-DE', 'french': 'fr-FR', 'fr': 'fr-FR', 'spanish': 'es-ES', 'es': 'es-ES', 'italian': 'it-IT', 'it': 'it-IT', 'japanese': 'ja-JP', 'ja': 'ja-JP' };
          const sttLang = langMap[currentLanguage?.toLowerCase()] || 'en-US';
          startSTT(wsUrl, { language: sttLang }, ({ text, isPartial }) => { if (text) { const t = Array.isArray(text) ? text.join(' ') : String(text); if (isPartial) { const display = (window._bscFinalText || '') + (window._bscFinalText ? ' ' : '') + t; setUserText(display); window._bscTranscript = display; } else { window._bscFinalText = (window._bscFinalText || '') + (window._bscFinalText ? ' ' : '') + t; setUserText(window._bscFinalText); window._bscTranscript = window._bscFinalText; } } });
        } else {
          const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
          if (SpeechRecognition) {
            const recognition = new SpeechRecognition();
            recognition.continuous = true; recognition.interimResults = true;
            const lc = currentLanguage?.toLowerCase();
            recognition.lang = (lc === 'german' || lc === 'de') ? 'de-DE' : (lc === 'french' || lc === 'fr') ? 'fr-FR' : (lc === 'spanish' || lc === 'es') ? 'es-ES' : (lc === 'italian' || lc === 'it') ? 'it-IT' : (lc === 'japanese' || lc === 'ja') ? 'ja-JP' : 'en-US';
            recognition.onresult = (event) => { let t = ''; for (let i = 0; i < event.results.length; i++) t += event.results[i][0].transcript; setUserText(t); window._bscTranscript = t; };
            recognition.onerror = (e) => console.error('Camera STT error:', e);
            recognition.start(); window._bscRecognition = recognition;
          }
        }
      } catch (err) { console.error('Camera access denied:', err); }
    }
  };

  // TTS: read agent response aloud
  const readResponse = async (text) => {
    if (endedRef.current) return;
    lastReadResponse.current = text;
    stopAvatarSpeech();
    setAvatarMuted(false); // Show reading icon
    setSpeakerLocked(false); // Unlock speaker for new response
    setIsSpeaking(true);
    const langMap = { 'german': 'de', 'de': 'de', 'french': 'fr', 'fr': 'fr', 'spanish': 'es', 'es': 'es', 'italian': 'it', 'it': 'it', 'japanese': 'ja', 'ja': 'ja' };
    const voiceMap = { de: 'Hans', fr: 'Mathieu', es: 'Enrique', it: 'Giorgio', ja: 'Takumi', en: 'Matthew' };
    const langCode = langMap[currentLanguage?.toLowerCase()] || 'de';
    const voice = selectedAvatar === 'nebula' ? { de: 'Marlene', fr: 'Celine', es: 'Conchita', it: 'Carla', ja: 'Mizuki', en: 'Joanna' }[langCode] : voiceMap[langCode];
    try {
      await speakWithAvatar(text, null, voice || "Hans");
    } catch (e) { console.error("TTS error:", e); }
    // Stop audio if discussion ended while TTS was loading
    if (endedRef.current) { stopAvatarSpeech(); return; }
    setIsSpeaking(false);
  };

  // Send user response to agent with session_id
  const sendUserResponse = async (text) => {
    if (endedRef.current) { console.log('sendUserResponse blocked: endedRef is true'); return; }
    const textStr = String(text || '');
    if (!textStr.trim() || !sessionId) { console.log('sendUserResponse blocked: empty text or no sessionId', { text: textStr, sessionId }); return; }
    const userName = getUserFirstName();
    setConversationHistory(prev => [...prev, { type: 'user', name: userName, text: textStr }]);
    saveConversationLog(userName, textStr, "", "", "");
    setAgentResponse('');
    setUserText('');
    setIsLoading(true);
    setSuggestion('');
    setHint('');
    setHasHint(false);
    setShowHint(false);

    const langMap = { 'german': 'German', 'de': 'German', 'french': 'French', 'fr': 'French', 'spanish': 'Spanish', 'es': 'Spanish', 'italian': 'Italian', 'it': 'Italian', 'japanese': 'Japanese', 'ja': 'Japanese' };
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/lb-api';
      const res = await fetch(`${apiUrl}/business-scenario/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId, language: langMap[currentLanguage?.toLowerCase()] || 'German', prompt: text })
      });
      const data = await res.json();
      console.log('Agent response:', data);
      if (checkAuthError(data)) return;
      const agentData = data.data?.content || data.data || data;
      // Check if agent session expired
      if (agentData.error && String(agentData.error).includes('not found')) {
        setAgentResponse('Session expired. Please start a new discussion.');
        setConversationHistory(prev => [...prev, { type: 'assistant', name: avatarName, text: 'Session expired. Please start a new discussion.' }]);
        setIsLoading(false);
        return;
      }
      setSuggestion(agentData.suggestion || '');
      setHint(agentData.hint || '');
      setHasHint(!!agentData.hint);
      const respText = agentData.response || '';
      setAgentResponse(respText);
      setAvatarMuted(false);
      setConversationHistory(prev => [...prev, { type: 'assistant', name: avatarName, text: respText }]);
      saveConversationLog(avatarName, "", respText, agentData.suggestion, agentData.hint);
      if (respText) readResponse(respText);
    } catch (err) {
      console.error('Agent error:', err);
      setAgentResponse('Sorry, something went wrong. Please try again.');
    }
    setIsLoading(false);
  };

  // When mic stops, send the captured text
  const onMicStop = (transcript) => {
    if (endedRef.current) return; // Don't send if discussion ended
    const t = String(transcript || '');
    if (t.trim()) {
      setUserText(t);
      sendUserResponse(t);
    }
  };

  const saveConversationLog = async (sender, userInput, agentResp, sug, hnt) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/lb-api';
      await fetch(`${apiUrl}/business-scenario/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          enterprise_id: user?.email || 'local-user',
          session_id: sessionId,
          scenario_id: scenarioId,
          selceted_lang: currentLanguage,
          selected_level: currentLevel,
          sender,
          user_input: userInput || '',
          agent_response: agentResp || '',
          suggestion: sug || '',
          hint: hnt || '',
          avatar: avatarName
        })
      });
    } catch (e) { console.error('Save log error:', e); }
  };

  const fetchTranscript = async () => {
    if (!sessionId) return;
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/lb-api';
      const res = await fetch(`${apiUrl}/business-scenario/logs?session_id=${sessionId}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) console.log('Transcript logs fetched:', data.logs.length);
    } catch (e) { console.error('Fetch transcript error:', e); }
  };

  const handleKeyPress = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } };

  // Main conversation page
  return (
    <div className="bs-conversation-page">
      <Header user={user} />
      <div className="nav-header-container">
        <SideNav />
        <PageHeader breadcrumbs={breadcrumbs} language={currentLanguage} proficiencyLevel={currentLevel} onLanguageChange={(lang) => setCurrentLanguage(lang)} onLevelChange={(level) => setCurrentLevel(level)} disableLevelDropdown={true} />
      </div>

      <main className="bs-conversation-content">
        <div className="bsc-top-bar">
          <h1 className="bsc-title">Business Scenario: {businessScenario} </h1>
        </div>
        <div className="bsc-full-divider"></div>

        <div className="bsc-body">
          <div className="bsc-left">
            <div className="bsc-prompt-row">
              
              <div className="bsc-duration">
                <img src="/images/blacktime.svg" alt="" width="20" height="20" />
                <span>Duration: {formatTime(duration)}</span>
                <div className="bsc-menu-wrapper" ref={durationMenuRef}>
                  <button className="bsc-menu-btn" onClick={(e) => { const rect = e.currentTarget.getBoundingClientRect(); setMenuPos({top: rect.bottom + 4, left: rect.right - 240}); setShowDurationMenu(!showDurationMenu); }}>
                    <img src="/images/verticaldots.svg" alt="" width="20" height="20" />
                  </button>
                  {showDurationMenu && ReactDOM.createPortal(
                    <div className="bsc-duration-dropdown" style={{position:"fixed", top: menuPos.top + "px", left: menuPos.left + "px"}} onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
                      <button className="bsc-duration-item" onClick={() => { setCaptionsOn(!captionsOn); setTimeout(() => setShowDurationMenu(false), 400); }}>
                        <span>Captions</span>
                        <span className={`bsc-toggle${captionsOn ? " on" : ""}`}><span className="bsc-toggle-knob"></span></span>
                      </button>
                      <button type="button" className="bsc-duration-item bsc-transcript-trigger" style={{cursor: started ? "pointer" : "default", color: started ? "#000" : "#aaa"}} onClick={() => { console.log("TRANSCRIPT CLICKED, started:", started, "sessionId:", sessionId); if (started && sessionId) { setShowPausePopup(true); if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } } setShowDurationMenu(false); }}>Conversation Transcript</button>
                    </div>,
                    document.body
                  )}
                </div>
              </div>
            </div>

            <div className="bsc-avatar-area">
              <img src="/images/BSBg1.svg" alt="" className="bsc-bg1" aria-hidden="true" />
              <img src="/images/BSbg2.svg" alt="" className="bsc-bg2" aria-hidden="true" />
              <div className="bsc-avatars">
                <div className={`bsc-avatar-card${isLoading || (!micActive && started) ? ' highlighted' : ''}`}>
                  <div className="bsc-avatar-circle soren">
                    <img src={selectedAvatar === "soren" ? "/images/MaleAvatar.svg" : "/images/FemaleAvatar.svg"} alt={avatarName} className="bsc-avatar-inner" />
                  </div>
                  <div className="bsc-avatar-bottom-icon">
                    <img src={avatarMuted ? "/images/speaker-mute.svg" : "/images/speakerblack.svg"} alt="" width="40" height="40" onClick={() => {
                      if (!hasResponse || speakerLocked) return;
                      if (!avatarMuted) {
                        // Stop reading
                        if (getCurrentAudio()) getCurrentAudio().pause();
                        stopAvatarSpeech();
                        setAvatarMuted(true);
                      } else {
                        // Resume/re-read
                        setAvatarMuted(false);
                        if (getCurrentAudio() && !getCurrentAudio().ended && lastReadResponse.current === agentResponse) {
                          getCurrentAudio().play().catch(console.error);
                        } else if (agentResponse) {
                          readResponse(agentResponse);
                        }
                      }
                    }} style={{cursor: (hasResponse && !speakerLocked) ? "pointer" : "default", opacity: (hasResponse && !speakerLocked) ? 1 : 0.4}} />
                  </div>
                  <p className="bsc-avatar-name">{avatarName}</p>
                </div>

                <div className={`bsc-avatar-card${micActive ? ' highlighted' : ''}`}>
                  <div className="bsc-avatar-circle victor">
                    {cameraOn ? (
                      <video ref={videoRef} autoPlay muted playsInline className="bsc-camera-feed" />
                    ) : (
                      <span className="bsc-avatar-vs">{getUserInitials()}</span>
                    )}
                  </div>
                  <div className="bsc-avatar-bottom-icons">
                    <button className="bsc-mic-btn" onClick={(e) => { e.stopPropagation(); if (!hasResponse) return; toggleMic(); }} style={{opacity: hasResponse ? 1 : 0.4, cursor: hasResponse ? "pointer" : "default"}}>
                      <span className="bsc-icon-circle"><img src="/images/Ellipseblack.svg" alt="" className="bsc-icon-circle-bg" /><img src={micActive ? "/images/unmuteMic-voice.svg" : "/images/Mic-voice.svg"} alt="Mic" width="16" height="16" className="bsc-icon-center" /></span>
                    </button>
                    <button className="bsc-mic-btn" onClick={(e) => { e.stopPropagation(); if (!hasResponse) return; toggleCamera(); }} style={{opacity: hasResponse ? 1 : 0.4, cursor: hasResponse ? "pointer" : "default"}}>
                      <span className="bsc-icon-circle"><img src="/images/Ellipseblack.svg" alt="" className="bsc-icon-circle-bg" /><img src={cameraOn ? "/images/camaraicon.svg" : "/images/stopcamaraicon.svg"} alt="Camera" width="16" height="16" className="bsc-icon-center" /></span>
                    </button>
                  </div>
                  <p className="bsc-avatar-name">{getUserFirstName()}</p>
                </div>
              </div>
            </div>

            {(conversationHistory.length > 0 || isLoading) && captionsOn && (
              <div className="bsc-conversation-log">
                {(micActive || cameraOn) && userText ? (
                  <p className="bsc-agent-response user"><strong>{getUserFirstName()}:</strong> {userText}</p>
                ) : isLoading ? (
                  <div className="bsc-agent-response loading"><span className="bsc-loading-dots"><span></span><span></span><span></span></span></div>
                ) : agentResponse ? (
                  <p className="bsc-agent-response assistant"><strong>{avatarName}:</strong> {agentResponse}</p>
                ) : null}
              </div>
            )}

            <div className="bsc-start-row">
              {started && (
                <button className="bsc-pause-play-btn" onClick={() => {
                  if (showPausePopup) {
                    // Resume
                    setShowPausePopup(false);
                    timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
                  } else {
                    // Pause
                    setShowPausePopup(true);
                    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
                  }
                }}>
                  <img src={showPausePopup ? "/images/bsplayicon.svg" : "/images/bspause.svg"} alt={showPausePopup ? "Play" : "Pause"} width="20" height="20" />
                </button>
              )}
              <button className="bsc-start-btn" onClick={started ? () => setShowEndConfirm(true) : handleStartDiscussion}>
                {started ? 'End Discussion' : 'Start Discussion'}
              </button>
            </div>
          </div>

          <div className="bsc-right">
            <div className="bsc-suggestions-panel">
              <div className="bsc-suggestions-header">
                <h3 className="bsc-suggestions-title">Realtime Suggestions</h3>
                <button className={`bsc-hints-btn${hasHint ? " active" : ""}`} disabled={!hasHint} onClick={() => setShowHint(!showHint)}>Hints</button>
              </div>
              {!suggestion && !showHint ? (
              <>
                <div className="bsc-suggestions-illustration">
                  <img src="/images/realtimebg.svg" alt="" />
                </div>
                <p className="bsc-suggestions-text">Live suggestions that support you in forming better, more natural sentences as you converse.</p>
              </>
            ) : (
              <div className="bsc-suggestions-feed">
                {suggestion && (
                  <div className="bsc-suggestion-item">
                    <img src="/images/suggestion.svg" alt="" width="16" height="16" />
                    <span>Suggestion: {suggestion}</span>
                  </div>
                )}
                {showHint && hint && (
                  <div className="bsc-hint-item">
                    <img src="/images/hints.svg" alt="" width="16" height="16" />
                    <span>Hint: {hint}</span>
                  </div>
                )}
              </div>
            )}
            </div>
            
          </div>
        </div>
      </main>

      <Footer />

      {showPausePopup && !showTranscript && (
        <div className="bsc-confirm-overlay">
          <div className="bsc-confirm-modal bsc-pause-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="bsc-pause-title">Conversation has been paused.</h3>
            <p className="bsc-pause-desc">Click Resume to continue within 15 minutes or View Transcript of conversation till now.</p>
            <div className="bsc-confirm-actions">
              <button className="bsc-confirm-cancel" onClick={() => { setShowPausePopup(false); timerRef.current = setInterval(() => setDuration(d => d + 1), 1000); }}>Resume</button>
              <button className="bsc-confirm-yes" onClick={() => { fetchTranscript(); setShowTranscript(true); }}>View Transcript</button>
            </div>
          </div>
        </div>
      )}

      {showTranscript && (
        <div className="bsc-confirm-overlay" onClick={() => setShowTranscript(false)}>
          <div className="bsc-transcript-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bsc-transcript-header">
              <h3>Conversation Transcript</h3>
              <button className="bsc-transcript-close" onClick={() => setShowTranscript(false)}>&times;</button>
            </div>
            <div className="bsc-transcript-notice">
              <span>Transcript audio will be generated after the conversation</span>
              <div className="bsc-qs-audio">
                <img src="/images/audioreload.svg" alt="" width="16" height="16" style={{opacity:0.5}} />
                <img src="/images/audioplayer.svg" alt="" height="18" /><img src="/images/audioplayer.svg" alt="" height="18" /><img src="/images/audioplayer.svg" alt="" height="18" />
                <span>00.00 sec</span>
              </div>
            </div>
            <h4 className="bsc-transcript-prompt">{businessScenario}</h4>
            <div className="bsc-transcript-messages">
              {conversationHistory.map((msg, i) => (
                <div key={i} className="bsc-transcript-msg">
                  <span className={`bsc-transcript-sender ${msg.type === "assistant" ? "agent" : "user"}`}>
                    {msg.type === "assistant" ? avatarName + ":" : getUserFirstName() + ":"}
                  </span>
                  <span className="bsc-transcript-text">{msg.text}</span>
                </div>
              ))}
            </div>
            <div className="bsc-transcript-footer">
              <button className="bsc-confirm-cancel" onClick={() => { setShowTranscript(false); }}>Close</button>
              <button className="bsc-confirm-yes" onClick={() => { setShowTranscript(false); timerRef.current = setInterval(() => setDuration(d => d + 1), 1000); setShowPausePopup(false); }}>Resume Conversation</button>
            </div>
          </div>
        </div>
      )}

      {showEndConfirm && (
        <div className="bsc-confirm-overlay" onClick={() => setShowEndConfirm(false)}>
          <div className="bsc-confirm-modal bsc-end-confirm" onClick={(e) => e.stopPropagation()}>
            <p className="bsc-confirm-text">Are you sure you want to end discussion?</p>
            <div className="bsc-confirm-actions">
              <button className="bsc-confirm-cancel" onClick={() => setShowEndConfirm(false)}>Cancel</button>
              <button className="bsc-confirm-yes" onClick={handleEndDiscussion}>Yes</button>
            </div>
          </div>
        </div>
      )}

      {showSummaryLoading && (
        <div className="bsc-confirm-overlay">
          <div className="bsc-confirm-modal bsc-loading-modal" onClick={(e) => e.stopPropagation()}>
            <button className="bsc-loading-close" onClick={() => setShowSummaryLoading(false)}>&times;</button>
            <img src="/images/loadingicon.svg" alt="" className="bsc-loading-icon" />
            <p className="bsc-loading-text">Creating your conversation summary.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessConversation;